// Lightweight cross-tab navigation for the AdminDashboard.
//
// TM Client Coverage links use this to "jump to" the corresponding row in
// the CRM Accounts tab or the eCards tab, pre-filling the search box on
// the destination page so the matched row is immediately visible.

export type AdminTab =
  | "closedcheck"
  | "matchhub"
  | "summary"
  | "timesheets"
  | "drivers"
  | "fleet"
  | "fleetpulse"
  | "roster"
  | "jobinput"
  | "reports"
  | "crm"
  | "exportbuilder"
  | "ecard"
  | "payout"
  | "insurance"
  | "settings";

export interface AdminNavDetail {
  tab: AdminTab;
  search?: string;
  path?: string[];
  /** CRM only: account_id whose detail panel should open straight away. */
  openAccount?: string;
}

export const ADMIN_NAV_EVENT = "app:admin-navigate";
export const CRM_FOCUS_KEY = "crm:focus-search";
export const CRM_OPEN_ACCOUNT_KEY = "crm:open-account";
export const ECARD_FOCUS_KEY = "ecard:focus-search";
export const NAV_PATH_KEY = "admin:nav-path";

/**
 * `path` is a stack of nested sub-tab values, outermost first (e.g.
 * `["fleet-defaults", "fleet-checks"]` for Settings -> Fleet Defaults ->
 * Fleet Checks) -- lets the Site Map (src/components/settings/AdminSiteMap.tsx)
 * deep-link into whatever nested tab state a destination screen owns.
 */
export function navigateAdmin(tab: AdminTab, search?: string, path?: string[]) {
  // Persist focus search / nav path so the destination page picks it up on
  // mount even if its component isn't yet rendered when the event fires.
  try {
    if (tab === "crm") {
      if (search) sessionStorage.setItem(CRM_FOCUS_KEY, search);
      else sessionStorage.removeItem(CRM_FOCUS_KEY);
    }
    if (tab === "ecard") {
      if (search) sessionStorage.setItem(ECARD_FOCUS_KEY, search);
      else sessionStorage.removeItem(ECARD_FOCUS_KEY);
    }
    if (path && path.length) sessionStorage.setItem(NAV_PATH_KEY, JSON.stringify(path));
    else sessionStorage.removeItem(NAV_PATH_KEY);
  } catch {
    /* ignore */
  }

  window.dispatchEvent(
    new CustomEvent<AdminNavDetail>(ADMIN_NAV_EVENT, {
      detail: { tab, search, path },
    })
  );
}

/** Jumps to the CRM tab and opens that client's detail panel (the list is
 * also filtered to the account number behind it). */
export function openCrmAccount(accountId: string) {
  try {
    sessionStorage.setItem(CRM_FOCUS_KEY, accountId);
    sessionStorage.setItem(CRM_OPEN_ACCOUNT_KEY, accountId);
    sessionStorage.removeItem(NAV_PATH_KEY);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(
    new CustomEvent<AdminNavDetail>(ADMIN_NAV_EVENT, {
      detail: { tab: "crm", search: accountId, openAccount: accountId },
    })
  );
}

export function consumeFocusSearch(key: string): string | null {
  try {
    const v = sessionStorage.getItem(key);
    if (v) sessionStorage.removeItem(key);
    return v;
  } catch {
    return null;
  }
}

/** Mirrors consumeFocusSearch -- returns the nested-tab path from the most
 * recent navigateAdmin() call, if any, and clears it so it isn't replayed. */
export function consumeNavPath(): string[] | null {
  try {
    const v = sessionStorage.getItem(NAV_PATH_KEY);
    if (v) {
      sessionStorage.removeItem(NAV_PATH_KEY);
      return JSON.parse(v);
    }
    return null;
  } catch {
    return null;
  }
}
